import { Calendar, ExternalLink, MousePointerClick, ShoppingCart, Wrench } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { CtaButtonData } from '@/Domain/api/Conversation/dto/Message.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';

type Props = {
  items: CtaButtonData[];
};

// Mirrors CtaButtonIcon in quickchat/messaging/inline_components.py; unknown icons render a plain button.
const CTA_ICONS: Record<string, LucideIcon> = {
  wrench: Wrench,
  'shopping-cart': ShoppingCart,
  calendar: Calendar,
  'external-link': ExternalLink,
};

export const InboxCtaCard = ({ items }: Props) => {
  const { t } = useTranslation('inbox_v2');

  const renderable = items.filter((item) => item.label && item.url);
  if (renderable.length === 0) return null;

  return (
    <div
      className="max-w-[320px] overflow-hidden rounded-xl border border-border bg-card shadow-sm"
      data-testid="cta-card"
    >
      {/* Visitor-language copy stays verbatim, so the card is framed as chat content rather than reply text. */}
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <MousePointerClick aria-hidden="true" className="size-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">
          {t('cta_card.shown_to_visitor')}
        </span>
      </div>

      <div className="flex flex-col items-start gap-3 px-4 py-3">
        {renderable.map((item, index) => {
          const Icon = item.icon ? CTA_ICONS[item.icon] : undefined;

          return (
            <Fragment key={index}>
              {item.description && (
                <p className="text-xs text-muted-foreground" data-testid="cta-button-description">
                  {item.description}
                </p>
              )}
              {/* rounded-lg matches InlineCtaButton in the widget so the operator sees the shape the visitor did. */}
              <Button
                asChild
                variant="dark"
                size="md"
                className="rounded-lg"
                data-testid="cta-button"
              >
                <a href={item.url} target="_blank" rel="noopener noreferrer">
                  {Icon && <Icon aria-hidden="true" />}
                  <span>{item.label}</span>
                </a>
              </Button>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};
